/**
 * Notification timing, stack depth, and slot routing — the single table
 * the whole host reads.
 *
 * # Why this is one table (feature-spec §0.1 D2, ratified 2026-07-26)
 *
 * Three documents specified toast timing three different ways, and two
 * stack depths:
 *
 *   | dimension | Flow F README | design-system.md | chrome-spec.md |
 *   | :-------- | :------------ | :--------------- | :------------- |
 *   | info      | 6 s           | 5 s              | 5 s            |
 *   | warning   | 10 s          | 8 s              | 8 s            |
 *   | error     | 10 s          | sticky           | sticky         |
 *   | critical  | never         | —                | banner         |
 *   | stack     | 5             | 4                | 4              |
 *
 * The architect ratified **``chrome-spec.md`` uniformly**: it is the
 * production-current chrome document (its 2026-05-29 production amendment
 * re-specified this layer), ``design-system.md`` agrees with it on every
 * row, and Flow F's README prose is the lone outlier on all of them.
 *
 * **ADR-029 §5's seconds are not a competing decision.** That table's
 * substantive content is *which surface* each SAPIENT priority raises —
 * ``toast-auto`` / ``toast-sticky`` / ``banner`` — which
 * :func:`alerts/alert-display.describeAlertPriority` already encodes and
 * which nothing here changes. Its parenthetical "6s" restated Flow F's
 * older number and is superseded on timing only. Adopting per-source
 * timings instead would put a 6 s alert toast beside a 5 s system toast on
 * one screen — a difference no operator reads as intentional.
 *
 * Cross-references:
 *   - ``storyboards/shared/chrome-spec.md`` §"Toast layer" (the adopted
 *     numbers) + §"Toast layer (production)" (geometry + treatment).
 *   - ADR-029 §5 (surface map — authoritative; timing superseded).
 *   - REQ-UI-056 (display-only mode) — the :data:`DISPLAY_ONLY_STICKY_EXPIRY_MS`
 *     refinement below.
 *   - feature-specs/alert-notification-surface/feature-spec.md §0.1 D2 / D4.
 */
import type { NotificationSeverity, NotificationSlot } from "@/notifications/notification-types";

/**
 * Maximum toasts visible at once. A 5th arrival evicts the oldest
 * (``chrome-spec.md`` — "max 4 visible. If a 5th would appear, the oldest
 * auto-dismisses to make room").
 *
 * Eviction is never silent data loss for a SAPIENT alert: the Alerts panel
 * is the durable record, and the alert stays there until dismissed.
 */
export const TOAST_STACK_CAP = 4;

/**
 * Auto-expiry applied to otherwise-sticky **toasts** in display-only mode
 * (REQ-UI-056 refinement, feature-spec §0.1 D4, ratified 2026-07-26).
 *
 * Display-only mode is for shared wall displays: toasts keep rendering but
 * **all interaction is disabled**, so a sticky toast has no dismissal path
 * and the stack would fill to :data:`TOAST_STACK_CAP` and then silently
 * evict — the surface meant to guarantee visibility becoming the one that
 * drops alerts. A long expiry lets the display self-heal.
 *
 * CRITICAL **banners** are deliberately exempt (they are meant to
 * interrupt, and multiples collapse behind the "+N more" affordance).
 */
export const DISPLAY_ONLY_STICKY_EXPIRY_MS = 60_000;

/**
 * Base auto-dismiss duration per severity, in ms. ``null`` means sticky —
 * the operator closes it.
 */
const _DURATION_MS_BY_SEVERITY: Readonly<Record<NotificationSeverity, number | null>> = {
  info: 5_000,
  success: 5_000,
  warning: 8_000,
  error: null,
  critical: null,
};

/**
 * Where a severity renders. ``critical`` takes the full-width sticky
 * banner under the topbar; everything else takes the bottom-right toast
 * stack (ADR-029 §5; ``chrome-spec.md`` §"Toast layer").
 */
export function notificationSlot(severity: NotificationSeverity): NotificationSlot {
  return severity === "critical" ? "banner" : "toast";
}

/** Options for {@link notificationDurationMs}. */
export interface DurationOptions {
  /**
   * Whether the UI is in display-only mode (REQ-UI-056). When true, a
   * sticky **toast** resolves to :data:`DISPLAY_ONLY_STICKY_EXPIRY_MS`
   * instead of staying forever; banners are unaffected.
   */
  readonly displayOnly?: boolean;
}

/**
 * Resolve the auto-dismiss duration for a severity, or ``null`` for
 * sticky.
 *
 * Banner-routed severities are always ``null`` — a CRITICAL banner
 * persists in every mode (feature-spec §0.1 D4). Toast-routed sticky
 * severities become :data:`DISPLAY_ONLY_STICKY_EXPIRY_MS` when
 * ``displayOnly`` is set.
 */
export function notificationDurationMs(
  severity: NotificationSeverity,
  opts: DurationOptions = {},
): number | null {
  const base = _DURATION_MS_BY_SEVERITY[severity];
  if (notificationSlot(severity) === "banner") {
    // A CRITICAL banner persists in every mode, display-only included.
    return null;
  }
  if (base === null && opts.displayOnly === true) {
    return DISPLAY_ONLY_STICKY_EXPIRY_MS;
  }
  return base;
}

/**
 * The ARIA live-region role for a severity (Flow F §Accessibility —
 * "``aria-live="polite"`` for non-critical, ``aria-live="assertive"`` for
 * critical").
 *
 * ``role="alert"`` carries an implicit ``aria-live="assertive"`` and
 * ``role="status"`` an implicit ``polite``, so the role alone is
 * sufficient — the host sets it and does not also hand-set ``aria-live``,
 * which would be redundant and can double-announce in some screen readers.
 *
 * ``error`` is assertive alongside ``critical``: an error-severity toast is
 * a HIGH SAPIENT alert (ADR-029 §5), which is operationally significant
 * enough to interrupt.
 */
export function notificationAriaRole(severity: NotificationSeverity): "status" | "alert" {
  return severity === "error" || severity === "critical" ? "alert" : "status";
}
